"use client";

import { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import { suggestionsDepuisBAN, vautLaPeineDeChercher, type SuggestionAdresse } from "@/lib/adresse-suggestions";

/**
 * UN CHAMP « ADRESSE » QUI PROPOSE — Base Adresse Nationale.
 *
 * On tape, les propositions s'affichent sous le champ ; on en choisit une,
 * elle remplit la voie, le code postal et la ville d'un coup. On peut aussi
 * ignorer la liste et garder ce qu'on a tapé : le champ reste un champ
 * texte, l'API n'est qu'une aide. Adresses françaises seulement — c'est ce
 * que la BAN connaît.
 */
export function AdresseAutocomplete({
    value,
    onChange,
    onSelect,
    className,
    placeholder,
    id,
}: {
    value: string;
    onChange: (valeur: string) => void;
    onSelect: (s: SuggestionAdresse) => void;
    className?: string;
    placeholder?: string;
    id?: string;
}) {
    const [propositions, setPropositions] = useState<SuggestionAdresse[]>([]);
    const [ouvert, setOuvert] = useState(false);
    const [actif, setActif] = useState(-1);
    const conteneur = useRef<HTMLDivElement>(null);
    // Après un choix, on ne relance pas la recherche sur la valeur qu'on vient
    // d'écrire : la liste se rouvrirait sur ce que l'on vient de fermer.
    const derniereChoisie = useRef<string | null>(null);

    useEffect(() => {
        if (value === derniereChoisie.current) return;
        if (!vautLaPeineDeChercher(value)) { setPropositions([]); return; }
        let annule = false;
        const t = setTimeout(async () => {
            try {
                const r = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(value.trim())}&limit=6&autocomplete=1`);
                if (!r.ok || annule) return;
                const data = await r.json();
                if (annule) return;
                setPropositions(suggestionsDepuisBAN(data?.features));
                setOuvert(true);
                setActif(-1);
            } catch {
                // Sans réseau, le champ reste un champ texte — rien à signaler.
            }
        }, 300);
        return () => { annule = true; clearTimeout(t); };
    }, [value]);

    useEffect(() => {
        const fermer = (e: MouseEvent) => {
            if (conteneur.current && !conteneur.current.contains(e.target as Node)) setOuvert(false);
        };
        document.addEventListener("mousedown", fermer);
        return () => document.removeEventListener("mousedown", fermer);
    }, []);

    const choisir = (s: SuggestionAdresse) => {
        derniereChoisie.current = s.adresse || value;
        onSelect(s);
        setOuvert(false);
        setPropositions([]);
    };

    return (
        <div ref={conteneur} className="relative">
            <input
                id={id}
                value={value}
                onChange={(e) => { derniereChoisie.current = null; onChange(e.target.value); }}
                onFocus={() => propositions.length > 0 && setOuvert(true)}
                onKeyDown={(e) => {
                    if (!ouvert || propositions.length === 0) return;
                    if (e.key === "ArrowDown") { e.preventDefault(); setActif((a) => Math.min(a + 1, propositions.length - 1)); }
                    else if (e.key === "ArrowUp") { e.preventDefault(); setActif((a) => Math.max(a - 1, 0)); }
                    else if (e.key === "Enter" && actif >= 0) { e.preventDefault(); choisir(propositions[actif]); }
                    else if (e.key === "Escape") setOuvert(false);
                }}
                placeholder={placeholder}
                autoComplete="off"
                className={className}
            />
            {ouvert && propositions.length > 0 && (
                <ul className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-lg glass-card border border-border shadow-xl">
                    {propositions.map((s, i) => (
                        <li key={`${s.libelle}-${i}`}>
                            <button
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => choisir(s)}
                                className={cn(
                                    "w-full text-left px-3 py-2 text-sm flex items-start gap-2 hover:bg-muted",
                                    i === actif && "bg-muted"
                                )}
                            >
                                <MapPin className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
                                <span className="text-foreground">{s.libelle}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
